/**
 * My Rewards Page
 * Displays user's reward instances in a sortable table with tab filtering
 */

import { useState, useMemo } from 'react'
import {
  Box,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
} from '@mui/material'
import {
  CheckCircle,
  Schedule,
  Redeem,
  AllInclusive,
  CardGiftcard,
  Warning,
} from '@mui/icons-material'
import { RewardInstanceStatus, getContentTypeName } from '@api/types/rewards'
import { useMyRewards } from '../hooks/useMyRewards'
import { RewardDetailDialog } from '../components/RewardDetailDialog'
import { RewardStatusChip } from '../components/RewardStatusChip'
import type { UserRewardDto } from '@api/types/rewards'
import {
  formatExpirationDate,
  getExpirationWarningLevel,
  getContentTypeColor,
} from '../utils/rewardHelpers'

type TabValue = 'active' | 'expired' | 'redeemed' | 'all'

type SortField = 'title' | 'contentType' | 'status' | 'remainingUses' | 'expiresAt' | 'claimedAt'
type SortOrder = 'asc' | 'desc'

const tabConfig: { value: TabValue; label: string; icon: JSX.Element; status?: RewardInstanceStatus }[] = [
  { value: 'active', label: 'Active', icon: <CheckCircle />, status: RewardInstanceStatus.Active },
  { value: 'expired', label: 'Expired', icon: <Schedule />, status: RewardInstanceStatus.Expired },
  { value: 'redeemed', label: 'Redeemed', icon: <Redeem />, status: RewardInstanceStatus.FullyRedeemed },
  { value: 'all', label: 'All', icon: <AllInclusive /> },
]

interface HeadCell {
  id: SortField
  label: string
  numeric: boolean
  width?: string | number
}

const headCells: HeadCell[] = [
  { id: 'title', label: 'Reward', numeric: false },
  { id: 'contentType', label: 'Type', numeric: false, width: 120 },
  { id: 'status', label: 'Status', numeric: false, width: 120 },
  { id: 'remainingUses', label: 'Uses', numeric: true, width: 100 },
  { id: 'expiresAt', label: 'Expires', numeric: false, width: 150 },
  { id: 'claimedAt', label: 'Claimed', numeric: false, width: 120 },
]

function formatClaimedDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString()
}

function compareValues<T>(a: T, b: T, orderBy: SortField, order: SortOrder): number {
  const aReward = a as UserRewardDto
  const bReward = b as UserRewardDto

  let aValue: string | number | null
  let bValue: string | number | null

  switch (orderBy) {
    case 'title':
      aValue = aReward.title?.toLowerCase() ?? ''
      bValue = bReward.title?.toLowerCase() ?? ''
      break
    case 'contentType':
      aValue = aReward.contentType
      bValue = bReward.contentType
      break
    case 'status':
      aValue = aReward.status
      bValue = bReward.status
      break
    case 'remainingUses':
      // Treat null (unlimited) as very high number for sorting
      aValue = aReward.remainingUses ?? 999999
      bValue = bReward.remainingUses ?? 999999
      break
    case 'expiresAt':
      // Treat null (never) as very far future for sorting
      aValue = aReward.expiresAt ? new Date(aReward.expiresAt).getTime() : Number.MAX_SAFE_INTEGER
      bValue = bReward.expiresAt ? new Date(bReward.expiresAt).getTime() : Number.MAX_SAFE_INTEGER
      break
    case 'claimedAt':
      aValue = new Date(aReward.claimedAt).getTime()
      bValue = new Date(bReward.claimedAt).getTime()
      break
    default:
      return 0
  }

  if (aValue < bValue) return order === 'asc' ? -1 : 1
  if (aValue > bValue) return order === 'asc' ? 1 : -1
  return 0
}

export default function MyRewardsPage() {
  const [activeTab, setActiveTab] = useState<TabValue>('active')
  const [selectedRewardId, setSelectedRewardId] = useState<string | null>(null)
  const [orderBy, setOrderBy] = useState<SortField>('claimedAt')
  const [order, setOrder] = useState<SortOrder>('desc')

  // Get the status filter for the current tab
  const currentTabConfig = tabConfig.find((t) => t.value === activeTab)
  const statusFilter = currentTabConfig?.status

  // Fetch rewards with the current filter
  const { data, isLoading, error, refetch } = useMyRewards({
    status: statusFilter,
  })

  const rewards = data?.items ?? []

  // Sort the rewards
  const sortedRewards = useMemo(() => {
    return [...rewards].sort((a, b) => compareValues(a, b, orderBy, order))
  }, [rewards, orderBy, order])

  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleRowClick = (reward: UserRewardDto) => {
    setSelectedRewardId(reward.id)
  }

  const handleCloseDialog = () => {
    setSelectedRewardId(null)
  }

  const handleTransferSuccess = () => {
    refetch()
  }

  return (
    <Box>
      {/* Tabs */}
      <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabConfig.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load rewards. Please try again later.
        </Alert>
      )}

      {/* Empty state */}
      {!isLoading && !error && rewards.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CardGiftcard sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Rewards Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 'active'
              ? "You don't have any active rewards yet. Complete adventures to earn rewards!"
              : activeTab === 'expired'
                ? "You don't have any expired rewards."
                : activeTab === 'redeemed'
                  ? "You haven't redeemed any rewards yet."
                  : "You don't have any rewards yet. Complete adventures to earn rewards!"}
          </Typography>
        </Paper>
      )}

      {/* Rewards table */}
      {!isLoading && !error && rewards.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{ width: headCell.width, fontWeight: 600 }}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRewards.map((reward) => {
                const expirationWarning = getExpirationWarningLevel(reward.expiresAt)
                const isActive = reward.status === RewardInstanceStatus.Active

                return (
                  <TableRow
                    key={reward.id}
                    hover
                    onClick={() => handleRowClick(reward)}
                    sx={{
                      cursor: 'pointer',
                      opacity: reward.status === RewardInstanceStatus.Expired ? 0.7 : 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    {/* Title */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isActive && (
                          <CardGiftcard fontSize="small" color="primary" />
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {reward.title || 'Untitled Reward'}
                          </Typography>
                          {reward.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 300,
                              }}
                            >
                              {reward.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Type */}
                    <TableCell>
                      <Chip
                        label={getContentTypeName(reward.contentType)}
                        size="small"
                        color={getContentTypeColor(reward.contentType)}
                        variant="outlined"
                      />
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <RewardStatusChip status={reward.status} />
                    </TableCell>

                    {/* Uses */}
                    <TableCell align="right">
                      <Typography variant="body2">
                        {reward.remainingUses === null ? '∞' : reward.remainingUses}
                      </Typography>
                    </TableCell>

                    {/* Expires */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {(expirationWarning === 'soon' || expirationWarning === 'very-soon') && (
                          <Warning
                            fontSize="small"
                            color={expirationWarning === 'very-soon' ? 'error' : 'warning'}
                          />
                        )}
                        <Typography
                          variant="body2"
                          color={
                            expirationWarning === 'very-soon'
                              ? 'error.main'
                              : expirationWarning === 'soon'
                                ? 'warning.main'
                                : 'text.secondary'
                          }
                        >
                          {formatExpirationDate(reward.expiresAt)}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Claimed */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatClaimedDate(reward.claimedAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reward Detail Dialog */}
      <RewardDetailDialog
        open={!!selectedRewardId}
        onClose={handleCloseDialog}
        rewardId={selectedRewardId}
        onTransferSuccess={handleTransferSuccess}
      />
    </Box>
  )
}
